import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'customers.tag.manage',
  category: 'customers',
  title: 'Gérer les tags et segments clients',
  intent:
    "Créer et organiser des tags pour segmenter votre base clients (VIP, professionnels, particuliers…), puis vous en servir pour filtrer, cibler vos campagnes et adapter l'accès à la réservation.",
  context: {
    why: "Segmenter sa base, c'est pouvoir parler au bon client au bon moment plutôt que d'envoyer le même message à tout le monde. Un tag est une étiquette que vous posez sur des clients pour les regrouper : une fois posé, il sert à trois choses — filtrer la liste pour retrouver un segment d'un coup d'œil, cibler une campagne e-mail ou SMS sur ce seul segment, et conditionner l'accès à certains créneaux de réservation. Propelsia distingue trois familles de tags. Les tags personnalisés, que vous créez à la main avec une couleur, pour vos propres segments (VIP, Pro, Particulier…). Les tags système, recalculés automatiquement selon l'activité du client (a-t-il un rendez-vous à venir ? est-il inactif depuis un moment ?), parfaits pour repérer et relancer les clients qui s'éloignent avant de les perdre. Et les tags Propelsia, des étiquettes spéciales gérées par la plateforme, comme « bloqué » pour empêcher un client problématique de réserver.",
    use_cases: [
      'Créer un tag « VIP » pour réserver une offre exclusive à vos meilleurs clients.',
      'Distinguer « Pro » et « Particulier » pour envoyer des campagnes au ton et au contenu différents.',
      'Repérer, grâce aux tags système, les clients sans rendez-vous depuis plusieurs mois et leur envoyer une relance.',
      'Restreindre certains créneaux à un tag de clients via les règles de réservation.',
    ],
  },
  required_scopes: ['establishment.tag.create', 'establishment.tag.update', 'establishment.tag.delete'],
  ui_entry_points: [{ route: '/customers/tags', element: 'Bouton « Ajouter + » de la liste des tags' }],
  api: {
    primary_operation_id: 'ProTagAppController_createProTag',
    related_operation_ids: [
      'ProTagAppController_getProTag',
      'ProTagAppController_updateProTag',
      'ProTagAppController_deleteProTag',
    ],
  },
  steps: [
    '**Ouvrez la gestion des tags** depuis la rubrique Clients, écran « Tags ». Vous y voyez vos tags regroupés par famille : personnalisés, système et Propelsia.',
    '**Créez un tag personnalisé** avec le bouton « Ajouter » : donnez-lui un nom et choisissez une couleur pour le repérer facilement.',
    '**Enregistrez** : le tag est disponible et prêt à être posé sur vos clients.',
    '**Appliquez-le à vos clients** depuis leur fiche, ou en action groupée sur une sélection depuis la liste des clients.',
    '**Servez-vous des tags système** (recalculés tout seuls) pour filtrer les clients inactifs et lancer une relance ; et du tag Propelsia « bloqué » pour empêcher un client de réserver.',
  ],
  gotchas: [
    "**Trois familles de tags, trois usages.** Les tags personnalisés sont les vôtres, créés à la main avec une couleur. Les tags système sont automatiques et gérés par Propelsia selon l'activité du client. Les tags Propelsia sont des étiquettes spéciales de la plateforme (comme « bloqué »). Seuls les tags personnalisés se renomment et se suppriment ; les deux autres familles sont pilotées par Propelsia.",
    "**Les tags système se recalculent tout seuls.** « Rendez-vous à venir », « pas de rendez-vous depuis +30 jours / +60 jours / +6 mois » : vous n'avez rien à faire, Propelsia met ces tags à jour au fil de l'activité. C'est l'outil idéal pour repérer un client qui s'éloigne et le relancer avant de le perdre.",
    "**Le tag Propelsia « bloqué » empêche la réservation en ligne.** C'est le moyen de bloquer un client qui pose problème : posez-lui ce tag et il ne pourra plus prendre de rendez-vous en ligne. Il se pose aussi en action groupée depuis la liste des clients.",
    '**Les tags servent au filtrage, au ciblage et aux règles de réservation.** Un même tag vous sert à retrouver un segment dans la liste, à viser ce segment dans une campagne, et à décider qui peut réserver quels créneaux. Pensez vos tags en fonction de ces trois usages.',
    "**Donnez des couleurs distinctes et parlantes.** La couleur n'est pas cosmétique : c'est ce qui vous permet de lire vos segments d'un coup d'œil dans la liste. Évitez deux tags de teintes trop proches.",
  ],
  related_features: ['customers.customer.update', 'marketing.campaign.email.create', 'agenda.availability.manage'],
  demo_script: {
    duration_target_s: 35,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Segmenter sa base, c’est parler au bon client au bon moment : voyons comment créer un tag.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/customers'),
        caption: 'Menu latéral, Clients : tout part de votre base.',
      },
      {
        action: 'click',
        target: TEST_IDS.customers_tags_nav,
        caption: 'En haut de la liste, l’onglet Tags regroupe toutes vos étiquettes.',
      },
      { action: 'click', target: TEST_IDS.tags_add, caption: 'On crée un tag personnalisé, le vôtre.', capture: true },
      { action: 'wait_for', target: fieldTestId('title'), caption: 'Le formulaire s’ouvre.' },
      {
        action: 'fill',
        target: fieldTestId('title'),
        value: 'VIP',
        caption: 'On le nomme, ici « VIP » : une couleur le rendra repérable d’un coup d’œil.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.modal_submit,
        caption: 'Votre tag est prêt à poser sur vos clients, pour filtrer un segment ou cibler une campagne.',
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Tags : champ en haut à droite, tag + couleur. 3 types — personnalisés ;
// > système (RDV à venir, pas de RDV depuis +30j/+60j/+6 mois → filtrer et relancer pour ne pas
// > perdre le client) ; Propelsia (bloquer un client s'il pose problème).
